import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import '../styles/url-lists.css';
import { useTranslation } from 'react-i18next';

/**
 * Targeting templates — the place to see and tidy what has been saved.
 *
 * Templates are created from an ad group (that is where the targeting is), so
 * this page deliberately cannot create one: it lists them, renames, deletes,
 * and shows what is actually inside, which is the thing nobody can remember a
 * week later.
 */

interface Template {
  id: number;
  name: string;
  line_item_type: string | null;
  settings_count: number;
  owner_type: string;
  owner_id: string;
  source_advertiser_id: string | null;
  from_other_client?: boolean;
  owner_name?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** Targeting keys that are ids inside one advertiser's account. */
const CLIENT_BOUND = new Set([
  'channels_included', 'channels_excluded', 'negative_keyword_lists',
  'proximity_lists', 'remarketing_audiences',
]);

// The English names this map used to hold now live in the catalogue as
// templates.key_<key>; the row carries that key, so the label is looked up by it
// and falls back to whatever the API sent for a key we don't know.

const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const TargetingTemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const tr = t;   // alias for the callbacks below, where `t` used to be a Template
  const { selectedAdvertiser } = useAdvertiser();
  const advertiserId = selectedAdvertiser?.advertiser_id &&
    selectedAdvertiser.advertiser_id !== '__all__' ? selectedAdvertiser.advertiser_id : '';

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Template | null>(null);
  const [details, setDetails] = useState<
    { key: string; label: string; items: string[]; count: number; client_bound: boolean }[] | null>(null);
  const [renaming, setRenaming] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      // No client picked means "everything I can reach" — templates belong to an
      // agency, so demanding a client before showing them made no sense.
      const response = await api.get('/api/sense/group-templates', {
        params: advertiserId ? { advertiser_id: advertiserId } : {},
      });
      setTemplates(response.data.templates || []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('templates.errLoad'));
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setSelected(null);
    setDetails(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advertiserId]);

  const open = async (tpl: Template) => {
    setSelected(tpl);
    setRenaming(tpl.name);
    setDetails(null);
    try {
      const response = await api.get(`/api/sense/group-templates/${tpl.id}`);
      setDetails(response.data.details || []);
    } catch {
      setDetails([]);
    }
  };

  const rename = async () => {
    if (!selected || !renaming.trim() || renaming.trim() === selected.name) return;
    setBusyId(selected.id);
    try {
      await api.patch(`/api/sense/group-templates/${selected.id}`, { name: renaming.trim() });
      await load();
      setSelected({ ...selected, name: renaming.trim() });
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('templates.errRename'));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (tpl: Template) => {
    if (!window.confirm(tr('templates.confirmDelete', { name: tpl.name }))) return;
    setBusyId(tpl.id);
    try {
      await api.delete(`/api/sense/group-templates/${tpl.id}`);
      if (selected?.id === tpl.id) { setSelected(null); setDetails(null); }
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('templates.errDelete'));
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(
    () => templates.filter((tpl) => tpl.name.toLowerCase().includes(search.toLowerCase())),
    [templates, search]
  );

  return (
    <>

      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('templates.title')}</h1>
          <p className="header-subtitle">{t('templates.subtitle')}</p>
        </div>
      </div>

      <div className="url-lists-layout">
        <aside className="url-lists-panel">
          <div className="panel-section">
            <div className="search-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="search-input"
                placeholder={t('templates.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="editor-empty"><p>{t('templates.loading')}</p></div>
          ) : filtered.length === 0 ? (
            <div className="editor-empty">
              <h3>{t(search ? 'templates.noMatch' : 'templates.noneYet')}</h3>
              <p>{t(search ? 'templates.tryDifferent' : 'templates.howToCreate')}</p>
            </div>
          ) : (
            filtered.map((tpl) => (
              <div
                key={tpl.id}
                className={`list-item ${selected?.id === tpl.id ? 'active' : ''}`}
                onClick={() => open(tpl)}
              >
                <div className="list-item-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" rx="1.5" />
                    <rect x="14" y="3" width="7" height="7" rx="1.5" />
                    <rect x="3" y="14" width="7" height="7" rx="1.5" />
                    <rect x="14" y="14" width="7" height="7" rx="1.5" />
                  </svg>
                </div>
                <div className="list-item-content">
                  <div className="list-item-name">{tpl.name}</div>
                  <div className="list-item-meta">
                    {tr('templates.settingsCount', { count: tpl.settings_count })}
                    {tpl.line_item_type ? ` · ${tpl.line_item_type}` : ''}
                    {tpl.owner_name ? ` · ${tpl.owner_name}` : ''}
                  </div>
                </div>
              </div>
            ))
          )}
        </aside>

        <div className="url-editor-panel">
          {error && <div className="editor-empty"><p>{error}</p></div>}

          {!selected ? (
            <div className="editor-empty">
              <h3>{t('templates.selectOne')}</h3>
              <p>{t('templates.selectOneHint')}</p>
            </div>
          ) : (
            <div className="editor-content">
              <h2>{t('templates.template')}</h2>

              <div className="form-group">
                <label>{t('templates.name')}</label>
                <div className="tpl-rename-row">
                  <input
                    type="text"
                    value={renaming}
                    onChange={(e) => setRenaming(e.target.value)}
                    onBlur={rename}
                    onKeyDown={(e) => { if (e.key === 'Enter') rename(); }}
                  />
                  <button
                    className="btn-danger"
                    disabled={busyId === selected.id}
                    onClick={() => remove(selected)}
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>

              <h3 className="form-section-title">{t('templates.whatItCarries')}</h3>
              {details === null ? (
                <p className="tpl-hint">{t('templates.loadingDots')}</p>
              ) : details.length === 0 ? (
                <p className="tpl-hint">{t('templates.empty')}</p>
              ) : (
                <div className="tpl-details">
                  {details.map((row) => (
                    <div key={row.key} className={`tpl-detail ${row.client_bound ? 'client-bound' : ''}`}>
                      <div className="tpl-detail-head">
                        <span className="tpl-detail-label">
                          {t(`templates.key_${row.key}`, { defaultValue: row.label })}
                        </span>
                        <span className="tpl-detail-count">{row.count}</span>
                      </div>
                      <div className="tpl-detail-items">
                        {row.items.map((item, i) => (
                          <span key={`${row.key}-${i}`} className="tpl-chip">{item}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {details?.some((r) => r.client_bound) && (
                <p className="tpl-hint">{t('templates.clientBoundHint')}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TargetingTemplatesPage;
